import React, { useState } from 'react';
import {
  Box, Button, Typography,
} from '@mui/material';
import { useStatusMessageDispatch } from '../../../hooks/StatusMessageContext';

interface SignerInviteProps {
  signerEmail?: string;
  isSignerVerified?: boolean;
  onInvite: (email: string) => Promise<boolean>;
  onCancel: () => Promise<boolean>;
}

export const SignerInvite: React.FC<SignerInviteProps> = ({
  signerEmail = '',
  isSignerVerified = false,
  onInvite,
  onCancel,
}) => {
  const { setStatusMessage } = useStatusMessageDispatch();
  const [ email, setEmail ] = useState(signerEmail);
  const [ hasSigner, setHasSigner ] = useState(Boolean(signerEmail));
  const [ loading, setLoading ] = useState(false);

  const handleInviteClick = async () => {
    setLoading(true);

    try {
      await onInvite(email.trim());
      setHasSigner(true);
      setStatusMessage({
        message: 'Invite sent',
        severity: 'success',
      });
    } catch {
      setHasSigner(false);
      setStatusMessage({
        message: 'Failed to send invite',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelClick = async () => {
    setLoading(true);

    try {
      await onCancel();
      setEmail('');
      setHasSigner(false);
      setStatusMessage({
        message: 'Invite canceled',
        severity: 'success',
      });
    } catch {
      setHasSigner(true);
      setStatusMessage({
        message: 'Failed to cancel invitation',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const shareDisclaimer = 'To share the credit application with a signing authority, enter their email below and click Invite Signer. They will receive a secure link to access and edit the application. Once shared, your progress will be saved and locked.';
  const signerVerifiedDisclaimer = 'The signer has verified their email. Your progress has been locked.';

  return (
    <Box
      sx={{
        padding: 2,
        marginBottom: 3,
        border: '1px solid #d4d4d4',
        borderRadius: 1,
        maxWidth: '600px', // Match the signature pad's max width
        width: '100%',
      }}
    >
      <Typography
        sx={{
          marginBottom: 1,
          fontStyle: 'italic',
          whiteSpace: 'pre-wrap',
        }}
        variant='body2'
      >
        {isSignerVerified ? signerVerifiedDisclaimer : shareDisclaimer}
      </Typography>
      <Box
        alignItems='center'
        display='flex'
        gap={2}
      >
        <input
          required
          className={hasSigner ? 'sd-input sd-text sd-input--readonly' : 'sd-input sd-text'}
          placeholder='Signer Email'
          readOnly={hasSigner || loading}
          title=''
          type='email'
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Button
          color='primary'
          disabled={loading || !email.trim() || isSignerVerified}
          sx={{
            padding: '4px 12px',
            minWidth: 'auto',
            whiteSpace: 'nowrap',
            lineHeight: 2.2,
          }}
          variant='contained'
          onClick={hasSigner ? handleCancelClick : handleInviteClick}
        >
          {loading ? 'Processing...' : (hasSigner ? 'Cancel Invitation' : 'Invite Signer')}
        </Button>
      </Box>
    </Box>
  );
};
